import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from "@angular/core";
import { Observable, firstValueFrom, of, throwError } from "rxjs";
import { finalize, map, switchMap } from "rxjs/operators";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import {
    resolveSaqFormDictionary,
    SaqFormDictionary
} from "../../../../../dictionaries/saq/saq_dictionary_registry";
import {
    QuestionResponseMutationResponse,
    QuestionResponsesService,
    QuestionResponseDto,
    QuestionResponsePayload,
    QuestionResponseSaveMode
} from "../../../../../services/question_responses.service";
import { LoginService } from "../../../../../services/login.service";
import { NavigationMenuSelection } from "../../../../../interfaces/navigation_menu.interface";
import { FormNavigationDispatcherService } from "../../../../../services/form_navigation_dispatcher.service";
import { buildFollowUpLink, RequirementFollowUpLink } from "../../../utils/follow_up_links";
import { FollowUpModalComponent } from "../../../utils/follow_up_modal.component";
import { NeedsReviewModalComponent } from "../../../utils/needs_review_modal.component";
import { getDefaultPdfLayoutMaxWidth, resolveSectionMaxWidth } from "../../../utils/pdf_layout.util";
import { shouldBypassAssessmentSubmissionRequirements } from "../../../utils/assessment_submission_requirements.util";
import { isFormTypeMatch } from "../section_1/form_type.util";
import {
    resolveQuestionReviewStatusClass,
    resolveQuestionReviewStatusLabel
} from "../../../utils/question_review_status.util";

type SaqSectionDictionary = Record<string, SaqSectionDetails>;

interface SaqFormSection {
    title?: string;
    nav_title?: string;
    requirements?: SaqSectionDictionary;
    pdf_layout?: string;
}

interface SaqSectionDetails {
    section_category?: string;
    section_requirement?: string;
    section_subtitle?: string;
    section_number?: string;
    section_description?: string;
    section_testing?: string;
    applicability_notes?: string;
}

const DEFAULT_SECTION_ID = "section_2b";
const FALLBACK_SECTION_TITLE = "Section 2: Self-Assessment Questionnaire";
const DEFAULT_MAX_WIDTH = getDefaultPdfLayoutMaxWidth();
const DEFAULT_UNSAVED_OPTION = 5;
function getFirstRequirementKey(dictionary: SaqFormDictionary, sectionId: string): string | null {
    const sectionEntry = dictionary[sectionId];
    const requirements = sectionEntry?.requirements;

    if (!requirements || typeof requirements !== "object") {
        return null;
    }

    const keys = Object.keys(requirements);
    return keys.length > 0 ? keys[0] : null;
}

const buildDefaultSelection = (dictionary: SaqFormDictionary): NavigationMenuSelection => ({
    section: DEFAULT_SECTION_ID,
    target: getFirstRequirementKey(dictionary, DEFAULT_SECTION_ID) ?? "1_1_1"
});

@Component({
    selector: "app-saq-question-main",
    templateUrl: "./main.component.html",
    styleUrls: ["./main.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaqQuestionMainComponent {
    private saqFormDictionary: SaqFormDictionary = resolveSaqFormDictionary(null);
    private currentSelection: NavigationMenuSelection = buildDefaultSelection(this.saqFormDictionary);
    private loadSequence = 0;
    private followUpModalRef: NzModalRef<FollowUpModalComponent> | null = null;
    private needsReviewModalRef: NzModalRef<NeedsReviewModalComponent> | null = null;
    private currentFormType: string | null = null;

    protected sectionDetails: SaqSectionDetails = this.lookupSectionDetails(
        this.currentSelection.section,
        this.currentSelection.target
    );
    protected sectionTitle = this.lookupSectionTitle(this.currentSelection.section);
    protected hasApplicabilityNotes = this.computeHasApplicabilityNotes(this.sectionDetails);
    protected contentMaxWidth = this.computeContentMaxWidth(this.currentSelection.section);
    protected selectedOption: number | null = null;
    protected resultsDescription = "";
    protected lastSavedAt: string | null = null;
    protected lastSavedByName: string | null = null;
    protected isLoading = false;
    protected isSaving = false;
    protected resultsDescriptionError: string | null = null;
    protected isMarkingNeedsReview = false;
    private savedSelectedOption: number | null = null;
    protected activeComplianceStatus: 0 | 1 | null = null;
    @Input() hasAssignedQa = false;
    @Input() isCurrentUserAssignedQa = false;
    @Input() qsaReviewedInput: 0 | 1 = 0;
    private hasReadOnlyRestriction = false;

    @Output()
    readonly responseSaved = new EventEmitter<QuestionResponseMutationResponse>();

    @Input()
    set selection(value: NavigationMenuSelection | null) {
        const normalized = this.normalizeSelection(value);
        this.currentSelection = normalized;
        this.sectionTitle = this.lookupSectionTitle(normalized.section);
        this.sectionDetails = this.lookupSectionDetails(normalized.section, normalized.target);
        this.hasApplicabilityNotes = this.computeHasApplicabilityNotes(this.sectionDetails);
        this.contentMaxWidth = this.computeContentMaxWidth(normalized.section);
        this.loadQuestionResponse(normalized);
    }

    @Input()
    set formType(value: string | null) {
        this.currentFormType = value;
        this.saqFormDictionary = resolveSaqFormDictionary(value);
        this.sectionTitle = this.lookupSectionTitle(this.currentSelection.section);
        this.sectionDetails = this.lookupSectionDetails(
            this.currentSelection.section,
            this.currentSelection.target
        );
        this.hasApplicabilityNotes = this.computeHasApplicabilityNotes(this.sectionDetails);
        this.contentMaxWidth = this.computeContentMaxWidth(this.currentSelection.section);
        this.markForCheck();
    }

    get formType(): string | null {
        return this.currentFormType;
    }

    @Input()
    isFormComplete = false;

    @Input()
    set readOnlyLicenseViewer(value: boolean) {
        this.hasReadOnlyRestriction = value === true;
        this.markForCheck();
    }

    get readOnlyLicenseViewer(): boolean {
        return this.hasReadOnlyRestriction;
    }

    @Input()
    set activeComplianceStatusInput(value: 0 | 1 | null) {
        this.activeComplianceStatus = value;
        this.markForCheck();
    }

    protected get sectionKey(): string {
        return this.currentSelection.target;
    }

    protected get sectionId(): string {
        return this.currentSelection.section;
    }

    protected get activeProductLicenseId(): number | null {
        return this.questionResponses.getActiveProductLicenseId();
    }

    constructor(
        private readonly questionResponses: QuestionResponsesService,
        private readonly notification: NzNotificationService,
        private readonly cdr: ChangeDetectorRef,
        private readonly loginService: LoginService,
        private readonly navigationDispatcher: FormNavigationDispatcherService,
        private readonly modal: NzModalService
    ) {}

    private lookupSectionDetails(sectionId: string, sectionKey: string): SaqSectionDetails {
        const requirements = this.getSectionRequirements(sectionId);
        return requirements[sectionKey] ?? {};
    }

    private lookupSectionTitle(sectionId: string): string {
        const sectionEntry = this.saqFormDictionary[sectionId];
        const title = typeof sectionEntry?.title === "string" ? sectionEntry.title.trim() : "";

        if (title.length > 0) {
            return title;
        }

        if (sectionId !== DEFAULT_SECTION_ID) {
            const defaultSection = this.saqFormDictionary[DEFAULT_SECTION_ID];
            const defaultTitle =
                typeof defaultSection?.title === "string" ? defaultSection.title.trim() : "";

            if (defaultTitle.length > 0) {
                return defaultTitle;
            }
        }

        return FALLBACK_SECTION_TITLE;
    }

    private computeContentMaxWidth(sectionId: string): number {
        return resolveSectionMaxWidth(this.saqFormDictionary, sectionId, DEFAULT_MAX_WIDTH);
    }

    private getSectionRequirements(sectionId: string): SaqSectionDictionary {
        const candidate = this.saqFormDictionary[sectionId]?.requirements;
        if (candidate && typeof candidate === "object") {
            return candidate;
        }

        const fallback = this.saqFormDictionary[DEFAULT_SECTION_ID]?.requirements;
        return fallback && typeof fallback === "object" ? fallback : {};
    }

    private computeHasApplicabilityNotes(details: SaqSectionDetails): boolean {
        const notes = details.applicability_notes;

        if (typeof notes !== "string") {
            return false;
        }

        return notes.trim().length > 0;
    }

    protected get followUpLink(): RequirementFollowUpLink | null {
        const savedOption = this.savedSelectedOption;

        if (savedOption === null) {
            return null;
        }

        if (this.selectedOption !== savedOption) {
            return null;
        }

        return buildFollowUpLink(
            savedOption,
            (sectionId) => getFirstRequirementKey(this.saqFormDictionary, sectionId),
            this.currentSelection
        );
    }

    protected get hasSelectedOption(): boolean {
        return this.selectedOption !== null;
    }

    protected get isResultsDescriptionRequired(): boolean {
        return !shouldBypassAssessmentSubmissionRequirements();
    }

    protected get disableNotTestedOption(): boolean {
        if (!isFormTypeMatch(this.currentFormType, "SAQ")) {
            return false;
        }

        return !isFormTypeMatch(this.currentFormType, "SAQ D");
    }

    protected onSelectedOptionChange(option: number | null): void {
        this.selectedOption = option;
    }

    protected onResultsDescriptionChange(value: string): void {
        this.resultsDescription = value;

        if (this.resultsDescriptionError && value.trim().length > 0) {
            this.setResultsDescriptionError(null);
        }
    }

    protected onFollowUpLinkActivated(selection: NavigationMenuSelection): void {
        this.navigationDispatcher.requestNavigation(selection);
    }

    protected openNeedsReviewModal(): void {
        if (!this.shouldShowNeedsReviewAction || this.isNeedsReviewDisabled) {
            return;
        }

        if (this.needsReviewModalRef) {
            return;
        }

        const modalRef = this.modal.confirm({
            nzTitle: "Mark as Needs Review",
            nzContent: `Are you sure you want to mark this assessment requirement as "Needs Review"?`,
            nzCentered: true,
            nzOkText: "Confirm",
            nzCancelText: "Cancel",
            nzOnOk: () => this.markNeedsReview()
        });

        this.needsReviewModalRef = modalRef;
        modalRef.afterClose.subscribe(() => {
            if (this.needsReviewModalRef === modalRef) {
                this.needsReviewModalRef = null;
            }
        });
    }

    private async markNeedsReview(): Promise<void> {
        this.isMarkingNeedsReview = true;
        this.markForCheck();

        try {
            const mutation = await firstValueFrom(
                this.questionResponses.mutateQuestionResponse(this.currentSelection, null, "needs_review")
            );
            this.activeComplianceStatus = mutation.savedResponse?.complianceStatus ?? 0;
            this.responseSaved.emit(mutation);
        } catch (error) {
            this.notification.error(
                "Unable to update response",
                "Something went wrong while marking this requirement as needing review."
            );
        } finally {
            this.isMarkingNeedsReview = false;
            this.markForCheck();
        }
    }

    private closeFollowUpModal(): void {
        if (!this.followUpModalRef) {
            return;
        }

        const modalRef = this.followUpModalRef;
        this.followUpModalRef = null;

        modalRef.destroy();
    }

    private openFollowUpModalIfNeeded(): void {
        const link = this.followUpLink;

        if (!link) {
            return;
        }

        this.closeFollowUpModal();
        const modalRef = this.modal.create<FollowUpModalComponent, { followUpLink: RequirementFollowUpLink }>({
            nzTitle: "Additional action required",
            nzContent: `This requirement requires further action to be taken in ${link.appendixLabel} due to your response of "${link.responseLabel}".`,
            nzCentered: true,
            nzData: { followUpLink: link },
            nzFooter: [
                {
                    label: `Go to ${link.appendixLabel}`,
                    type: "primary",
                    onClick: () => {
                        this.closeFollowUpModal();
                        this.navigationDispatcher.requestNavigation(link.targetSelection);
                    },
                },
                {
                    label: "Close",
                    onClick: () => this.closeFollowUpModal(),
                },
            ],
        });

        this.followUpModalRef = modalRef;

        modalRef.afterClose.subscribe(() => {
            if (this.followUpModalRef === modalRef) {
                this.followUpModalRef = null;
            }
        });
    }

    protected onSaveRequested(saveMode: QuestionResponseSaveMode = "reviewed"): void {
        if (this.isSaving || this.isLoading || this.isFormComplete || this.readOnlyLicenseViewer) {
            return;
        }

        if (!this.hasSelectedOption) {
            this.notification.error(
                "Unable to save response",
                "Please select a response option before saving this requirement.",
            );
            return;
        }

        const trimmedDescription = this.resultsDescription.trim();

        if (trimmedDescription.length === 0 && !shouldBypassAssessmentSubmissionRequirements()) {
            this.setResultsDescriptionError("Field required.");
            this.notification.error(
                "Unable to save response",
                "Please describe the results before saving this requirement.",
            );
            return;
        }

        this.setResultsDescriptionError(null);
        this.resultsDescription = trimmedDescription;

        const payload: QuestionResponsePayload = {
            selectedOption: this.selectedOption,
            resultsDescription: this.resultsDescription
        };

        this.isSaving = true;
        this.markForCheck();

        this.ensureSession()
            .pipe(
                switchMap(() => this.questionResponses.mutateQuestionResponse(this.currentSelection, payload, saveMode)),
                finalize(() => {
                    this.isSaving = false;
                    this.markForCheck();
                })
            )
            .subscribe({
                next: (mutation) => {
                    const response = mutation.savedResponse;
                    if (!response) {
                        this.notification.error(
                            "Save failed",
                            "The server response did not include saved requirement data."
                        );
                        return;
                    }
                    this.applyResponse(response);
                    this.responseSaved.emit(mutation);
                    this.notification.success("Response saved", "Your response has been saved successfully.");
                    this.openFollowUpModalIfNeeded();
                    this.markForCheck();
                },
                error: (error) => {
                    const message = error?.error?.message || error?.message || "Unable to save the response.";
                    this.notification.error("Save failed", message);
                }
            });
    }

    private ensureSession(): Observable<void> {
        if (this.loginService.isSessionValid()) {
            return of(void 0);
        }

        if (!this.loginService.isBypassLoginEnabled()) {
            return throwError(() => new Error("No active session. Please log in again."));
        }

        return this.loginService.ensureBypassLogin().pipe(
            map((success) => {
                if (!success) {
                    throw new Error("Unable to establish an authenticated session.");
                }
            })
        );
    }

    protected get isInteractionDisabled(): boolean {
        return this.isLoading || this.isSaving || this.readOnlyLicenseViewer;
    }

    private loadQuestionResponse(selection: NavigationMenuSelection): void {
        const requestId = ++this.loadSequence;
        this.isLoading = true;
        this.selectedOption = null;
        this.resultsDescription = "";
        this.resultsDescriptionError = null;
        this.lastSavedAt = null;
        this.lastSavedByName = null;
        this.savedSelectedOption = null;
        this.markForCheck();

        this.ensureSession()
            .pipe(
                switchMap(() => this.questionResponses.getQuestionResponse(selection)),
                finalize(() => {
                    if (this.loadSequence === requestId) {
                        this.isLoading = false;
                        this.markForCheck();
                    }
                })
            )
            .subscribe({
                next: (response) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    if (response) {
                        this.applyResponse(response);
                    } else {
                        this.selectedOption = DEFAULT_UNSAVED_OPTION;
                        this.resultsDescription = "";
                        this.lastSavedAt = null;
                        this.lastSavedByName = null;
                        this.savedSelectedOption = null;
                    }

                    this.markForCheck();
                },
                error: (error) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    const message = error?.error?.message || error?.message || "Unable to load the saved response.";
                    this.notification.error("Load failed", message);
                }
            });
    }

    private applyResponse(response: QuestionResponseDto): void {
        this.selectedOption = response.selectedOption ?? null;
        this.resultsDescription = response.resultsDescription ?? "";
        this.resultsDescriptionError = null;
        this.lastSavedAt = response.respondedAt ?? null;
        this.lastSavedByName = response.respondedByName ?? null;
        this.savedSelectedOption = this.selectedOption;
    }

    private setResultsDescriptionError(message: string | null): void {
        if (this.resultsDescriptionError === message) {
            return;
        }

        this.resultsDescriptionError = message;
        this.markForCheck();
    }

    protected get complianceStatusLabel(): string {
        if (this.isCompliancePending) {
            return "";
        }

        return this.isCompliant ? "Reviewed" : "Needs Review";
    }

    protected get qsaReviewStatusLabel(): string {
        return resolveQuestionReviewStatusLabel(this.qsaReviewedInput, this.isCompliancePending);
    }

    protected get qsaReviewStatusClass(): string {
        return resolveQuestionReviewStatusClass(this.qsaReviewedInput, this.isCompliancePending);
    }

    protected get complianceStatusClass(): string {
        if (this.isCompliancePending) {
            return "saq-question__status-indicator--pending";
        }

        return this.isCompliant
            ? "saq-question__status-indicator--compliant"
            : "saq-question__status-indicator--not-compliant";
    }

    protected get shouldShowNeedsReviewAction(): boolean {
        return this.activeComplianceStatus === 1 ||
            (this.hasAssignedQa && this.qsaReviewedInput === 1 && this.activeComplianceStatus === 0);
    }

    protected get shouldShowSaveAndMarkReviewedAction(): boolean {
        return this.activeComplianceStatus !== 1 && !this.isAssessorReviewedByAnotherUser;
    }

    private get isAssessorReviewedByAnotherUser(): boolean {
        return this.hasAssignedQa && !this.isCurrentUserAssignedQa && this.qsaReviewedInput === 1;
    }

    protected get isNeedsReviewDisabled(): boolean {
        return this.isSaving || this.isLoading || this.isFormComplete || this.readOnlyLicenseViewer || this.isMarkingNeedsReview;
    }

    private get isCompliant(): boolean {
        return this.activeComplianceStatus === 1;
    }

    private get isCompliancePending(): boolean {
        return this.isLoading;
    }

    private normalizeSelection(value: NavigationMenuSelection | null | undefined): NavigationMenuSelection {
        const section = this.normalizeSection(value?.section);
        const target = this.normalizeTarget(value?.target, section);
        return { section, target };
    }

    private normalizeSection(section: string | null | undefined): string {
        if (!section) {
            return buildDefaultSelection(this.saqFormDictionary).section;
        }

        const normalized = section.trim();
        if (normalized.length === 0) {
            return buildDefaultSelection(this.saqFormDictionary).section;
        }

        return this.hasSection(normalized) ? normalized : buildDefaultSelection(this.saqFormDictionary).section;
    }

    private normalizeTarget(target: string | null | undefined, sectionId: string): string {
        const fallbackTarget = this.getDefaultTargetForSection(sectionId);

        if (!target) {
            return fallbackTarget;
        }

        const normalized = target.trim();
        if (!normalized) {
            return fallbackTarget;
        }

        const requirements = this.getSectionRequirements(sectionId);
        return Object.prototype.hasOwnProperty.call(requirements, normalized) ? normalized : fallbackTarget;
    }

    private getDefaultTargetForSection(sectionId: string): string {
        const requirements = this.getSectionRequirements(sectionId);
        const keys = Object.keys(requirements);
        return keys[0] ?? buildDefaultSelection(this.saqFormDictionary).target;
    }

    private hasSection(sectionId: string): boolean {
        return Object.prototype.hasOwnProperty.call(this.saqFormDictionary, sectionId);
    }

    private markForCheck(): void {
        this.cdr.markForCheck();
    }
}
